import React, { ReactNode, useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { theme } from '../../../../theme/theme'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { societyManagementSettingsSchema } from '../../../../forms/schemas/society_management_settings'
import { NSocieties } from '../../../../interfaces/services/societies.interfaces'
import { Toggle } from '../../../../components/common/Toggle'
import { Label } from '../../../../components/common/Label'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { patchSociety } from '../../../../api/societies'

export const SocietySettingsView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('management:settings', {
      '{{ societyName }}': society?.name,
    })
  )

  const getDefaultValues = {
    postingDisabled: society?.residentPostingDisabledByAdmin || false,
    marketplacePostingDisabled:
      society?.settings?.marketplacePostingDisabled || false,
    chatOneOnOneDisabled: society?.settings?.chatOneOnOneDisabled || false,
    chatInterestsDisabled: society?.settings?.chatInterestsDisabled || false,
  }

  const { control, watch } = useForm({
    resolver: yupResolver(societyManagementSettingsSchema),
    defaultValues: getDefaultValues,
  })

  const { mutate: mutatePatchSociety, isPending: isUpdatingSociety } =
    useMutation({
      mutationFn: patchSociety,
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('societySettingsView.updateSettingsSuccess')
        )
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
      },
    })

  const sendPatch = useCallback(
    async (
      data: InferType<typeof societyManagementSettingsSchema>
    ): Promise<void> => {
      const body: NSocieties.NPatch.IRequestBody = {
        residentPostingDisabledByAdmin: data.postingDisabled,
        residentsCanPost: !data.postingDisabled,
        settings: {
          ...(society?.settings ? society.settings : {}),
          residentPostingDisabled: data.postingDisabled,
          marketplacePostingDisabled: data.marketplacePostingDisabled,
          chatOneOnOneDisabled: data.chatOneOnOneDisabled,
          chatInterestsDisabled: data.chatInterestsDisabled,
        },
      }
      if (society) {
        mutatePatchSociety({
          id: society?._id,
          body,
        })
      }
    },
    [society, mutatePatchSociety]
  )

  useEffect(() => {
    const subscription = watch((data) => {
      sendPatch(data)
    })
    return () => subscription.unsubscribe()
  }, [watch, sendPatch])

  const updatingSociety = isUpdatingSociety
  return (
    <ErrorBoundary>
      <ViewBase title={translate('societySettingsView.title')}>
        <div className="mt-6 w-full space-y-8">
          <div className="space-y-5">
            <p style={theme.textVariants.h3}>
              {translate('societySettingsView.feedSettings.title')}
            </p>
            <Controller
              control={control}
              name="postingDisabled"
              render={({ field: { value, onChange, name } }) => (
                <div
                  className="flex h-full w-full flex-col items-start justify-between
                  space-y-3 md:w-1/2 md:flex-row md:items-center md:space-x-10 md:space-y-0"
                >
                  <Label
                    name="postingDisabledLabel"
                    label={translate(
                      'societySettingsView.feedSettings.disablePostingTitle'
                    )}
                    style={theme.textVariants.baseBold}
                  />
                  <Toggle
                    name={name}
                    enabled={value || false}
                    onChange={onChange}
                    loading={updatingSociety}
                  />
                </div>
              )}
            />
            <p style={theme.textVariants.base} className="md:w-1/3">
              {translate(
                'societySettingsView.feedSettings.disablePostingDisclaimerText'
              )}
            </p>
            <Controller
              control={control}
              name="marketplacePostingDisabled"
              render={({ field: { value, onChange, name } }) => (
                <div
                  className="flex h-full w-full flex-col items-start justify-between
                  space-y-3 md:w-1/2 md:flex-row md:items-center md:space-x-10 md:space-y-0"
                >
                  <Label
                    name="marketplacePostingDisabledLabel"
                    label={translate(
                      'societySettingsView.feedSettings.marketplaceDisablePostingTitle'
                    )}
                    style={theme.textVariants.baseBold}
                  />
                  <Toggle
                    name={name}
                    enabled={value || false}
                    onChange={onChange}
                    loading={updatingSociety}
                  />
                </div>
              )}
            />
          </div>
          <div className="border-1 border-b md:w-1/2" />
          <div className="space-y-5">
            <p style={theme.textVariants.h3}>
              {translate('societySettingsView.chatSettings.title')}
            </p>
            <Controller
              control={control}
              name="chatOneOnOneDisabled"
              render={({ field: { value, onChange, name } }) => (
                <div
                  className="flex flex-col items-start justify-between space-y-3
                    md:w-1/2 md:flex-row md:items-center md:space-x-10 md:space-y-0"
                >
                  <Label
                    name="chatOneOnOneDisabledLabel"
                    label={translate(
                      'societySettingsView.chatSettings.disableOneOnOneTitle'
                    )}
                    style={theme.textVariants.baseBold}
                  />
                  <Toggle
                    name={name}
                    enabled={value || false}
                    onChange={onChange}
                    loading={updatingSociety}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="chatInterestsDisabled"
              render={({ field: { value, onChange, name } }) => (
                <div
                  className="flex flex-col items-start justify-between space-y-3 md:w-1/2
                    md:flex-row md:items-center md:space-x-10 md:space-y-0"
                >
                  <Label
                    name="chatInterestsDisabledLabel"
                    label={translate(
                      'societySettingsView.chatSettings.disableInterestsTitle'
                    )}
                    style={theme.textVariants.baseBold}
                  />
                  <Toggle
                    name={name}
                    enabled={value || false}
                    onChange={onChange}
                    loading={updatingSociety}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </ViewBase>
    </ErrorBoundary>
  )
}
